import type { ServicePlan, TeamMemberAssignment } from '@app/types';

// --- Team definitions (mockup-style groupings) ---

export interface TeamDefinition {
  id: string;
  name: string;
  icon: string; // lucide icon name
  roles: string[];
}

export const TEAM_DEFINITIONS: TeamDefinition[] = [
  {
    id: 'worship',
    name: 'Worship Team',
    icon: 'Music',
    roles: ['Worship Leader', 'Lead Vocals', 'Background Vocals', 'Harmony', 'Acoustic Guitar', 'Electric Guitar', 'Bass', 'Drums', 'Keys', 'Piano', 'Aux', 'Choir'],
  },
  {
    id: 'production',
    name: 'Production',
    icon: 'Video',
    roles: ['Sound Engineer', 'Lighting', 'Video', 'Projection', 'Live Stream', 'ProPresenter', 'Camera Operator', 'Stage Manager'],
  },
  {
    id: 'guest-services',
    name: 'Guest Services',
    icon: 'Users',
    roles: ['Greeter', 'Usher', 'Welcome Center', 'Parking', 'Security', 'Coffee Team'],
  },
];

// Flat lookup: all roles for a team
export const TEAM_ROLES_DICT: Record<string, string[]> = Object.fromEntries(
  TEAM_DEFINITIONS.map(t => [t.id, t.roles])
);

// Legacy alias kept for backward compat
export const SECTION_ROLES: Record<string, string[]> = {
  Vocals: ['Worship Leader', 'Lead Vocals', 'Background Vocals', 'Harmony'],
  Band: ['Acoustic Guitar', 'Electric Guitar', 'Bass', 'Drums', 'Keys', 'Piano', 'Aux'],
  'Audio/Visual': ['Sound Engineer', 'Lighting', 'Video', 'Projection', 'Live Stream'],
};

// --- Team member helpers ---

/** Get members belonging to a team definition */
export const getMembersForTeam = (team: TeamMemberAssignment[], def: TeamDefinition): TeamMemberAssignment[] => {
  return team.filter(t => def.roles.includes(t.role));
};

/** Group members by role within a team */
export const groupByRole = (members: TeamMemberAssignment[]): Record<string, TeamMemberAssignment[]> => {
  const groups: Record<string, TeamMemberAssignment[]> = {};
  for (const m of members) {
    if (!groups[m.role]) groups[m.role] = [];
    groups[m.role].push(m);
  }
  return groups;
};

/** Get active role slots for a team (roles that have at least one assignment) */
export const getActiveRoles = (team: TeamMemberAssignment[], def: TeamDefinition): string[] => {
  const roles = new Set<string>();
  for (const m of team) {
    if (def.roles.includes(m.role)) roles.add(m.role);
  }
  return Array.from(roles);
};

export const getTeamBySection = (team: TeamMemberAssignment[], section: string): TeamMemberAssignment[] => {
  return team.filter(t => SECTION_ROLES[section]?.includes(t.role));
};

export const getEmptySlots = (
  team: TeamMemberAssignment[],
  teamSlots: Record<string, number>,
  section: string
): number => {
  const assigned = getTeamBySection(team, section).length;
  const totalSlots = teamSlots[section] || 0;
  return Math.max(0, totalSlots - assigned);
};

// --- Plan helpers ---

export const hasPlan = (allPlans: ServicePlan[], selectedDate: Date, day: number): boolean => {
  return allPlans.some(p => {
    const planDate = new Date(p.date);
    return (
      planDate.getDate() === day &&
      planDate.getMonth() === selectedDate.getMonth() &&
      planDate.getFullYear() === selectedDate.getFullYear()
    );
  });
};

export const planCountForDay = (allPlans: ServicePlan[], selectedDate: Date, day: number): number => {
  return allPlans.filter(p => {
    const planDate = new Date(p.date);
    return (
      planDate.getDate() === day &&
      planDate.getMonth() === selectedDate.getMonth() &&
      planDate.getFullYear() === selectedDate.getFullYear()
    );
  }).length;
};

export const plansForDate = (allPlans: ServicePlan[], date: Date): ServicePlan[] => {
  const seen = new Set<string>();
  return allPlans
    .filter(p => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      const planDate = new Date(p.date);
      return (
        planDate.getDate() === date.getDate() &&
        planDate.getMonth() === date.getMonth() &&
        planDate.getFullYear() === date.getFullYear()
      );
    })
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
};

/** Group plans by date string for sidebar display */
export const groupPlansByDate = (plans: ServicePlan[]): Record<string, ServicePlan[]> => {
  const groups: Record<string, ServicePlan[]> = {};
  const seen = new Set<string>();
  for (const p of plans) {
    if (seen.has(p.id)) continue;
    seen.add(p.id);
    const d = new Date(p.date);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(p);
  }
  return groups;
};

export const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const formatTime12 = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const h = d.getHours();
  const m = d.getMinutes();
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${m.toString().padStart(2, '0')} ${period}`;
};
